import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Card, CircularProgress, Slider, Stack, TextField, type Theme, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceDot, ReferenceLine, XAxis, YAxis } from 'recharts';
import Button from '/@/components/button';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { planTicks } from './axis-ticks';
import { ChartFrame } from './density-curve';
import type { DensitySimulationSnapshot, SimulationRow, SimulationRowKey } from './density-simulation-data';
import DensitySimulationTable, { HABITUAL_BACKGROUND } from './density-simulation-table';
import { Figure, SectionLabel } from './hybrid-density-card';
import HybridRadioGroup from './hybrid-radio-group';
import { useDensitySimulation } from './use-density-simulation';
import type { ManagementHybrid } from './use-management-data';

/** Colours from the design with no theme token behind them. */
const CURVE_COLOR = '#00BCFF';
const RECOMMENDED_DOT_COLOR = '#00BCFF';
const HABITUAL_DOT_COLOR = '#624963';
const HABITUAL_ACCENT = '#443247';
const MARK_COLOR = '#10384F';
const AXIS_COLOR = 'rgba(16,56,79,0.12)';

const MAX_VARIABILITY = 25;
const VARIABILITY_MARKS = [0, 5, 10, 15, 20, 25].map((value) => ({ value, label: String(value) }));
const CHART_HEIGHT = 220;
const NOT_AVAILABLE = '-';

/** Accepts either decimal separator; the display unit may be pl/m², which is fractional. */
const parseDensityInput = (value: string) => {
    if (!value.trim()) return undefined;
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const ENVIRONMENT_LABELS: Record<SimulationRowKey, (value: string) => string> = {
    over: (value) => _('Ambiente sobrestimado: {{value}}', { value }),
    estimated: (value) => _('Ambiente estimado: {{value}}', { value }),
    under: (value) => _('Ambiente subestimado: {{value}}', { value }),
};

/**
 * What the copy-as-image render needs to draw this section as it's on screen: the hybrid in
 * view (its curve included), its snapshot, and the selector's options. The render can't fetch
 * or recompute any of it — see `DensitySimulationSnapshot`.
 */
export type DensitySimulationCopy = {
    hybrid: ManagementHybrid;
    snapshot: DensitySimulationSnapshot;
    hybrids: { id: number; catalogName: string }[];
    selectedId: number;
};

/** A point marked on the curve: which row it reads and where it sits, in chart pixels. */
type HoveredDot = { cx: number; cy: number; row: SimulationRow; density: number; yieldValue: number };

/** The design's tooltip, for one marked point. */
function SimulationTooltip({
    hybridName,
    environment,
    density,
    yieldValue,
}: {
    hybridName: string;
    environment: string;
    density: string;
    yieldValue: string;
}) {
    return (
        <Card elevation={1} sx={{ px: 1, py: 0.5, borderRadius: '2px' }}>
            <Typography variant='caption' component='p' fontWeight={700} color='primary'>
                {hybridName}
            </Typography>
            <Typography variant='caption' component='p' color='primary'>
                {environment}
            </Typography>
            <Typography variant='caption' component='p' fontWeight={700} color='primary'>
                {_('Densidad: {{density}}', { density })}
            </Typography>
            <Typography variant='caption' component='p' fontWeight={700} color='primary'>
                {_('Rendimiento: {{value}}', { value: yieldValue })}
            </Typography>
        </Card>
    );
}

/**
 * A marked point that owns its own hover. The tooltip only belongs on these six points, not
 * along the whole curve, which is what recharts' chart-wide `<Tooltip>` would give.
 */
function MarkedDot({
    cx,
    cy,
    fill,
    onEnter,
    onLeave,
}: {
    cx?: number;
    cy?: number;
    fill: string;
    onEnter: (position: { cx: number; cy: number }) => void;
    onLeave: () => void;
}) {
    if (cx === undefined || cy === undefined) return null;
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: hover-only aid; every value it shows is also in the table below
        <g onMouseEnter={() => onEnter({ cx, cy })} onMouseLeave={onLeave} style={{ cursor: 'pointer' }}>
            {/* A wider invisible target than the 4px dot, so it's easy to land on. */}
            <circle cx={cx} cy={cy} r={10} fill='transparent' />
            <circle cx={cx} cy={cy} r={4} fill={fill} stroke='white' />
        </g>
    );
}

function LegendLine({ dashed }: { dashed?: boolean }) {
    return <Box sx={{ width: 24, borderTop: `1px ${dashed ? 'dashed' : 'solid'}`, borderColor: MARK_COLOR }} />;
}

/**
 * The section as drawn: header, the recommended and habitual density cards, the variability,
 * the comparative curve and the yields table — everything read off one hybrid and its snapshot.
 *
 * Presentational only, so the wizard, the result page and the PDF all draw the same thing. The
 * parts that differ between them — the habitual density field, the slider, the copy button and
 * the hybrid selector — come in as slots.
 */
export function DensitySimulationView({
    hybrid,
    snapshot,
    habitual,
    variability,
    slider,
    headerAction,
    selector,
    chartWidth,
    compact = false,
}: {
    hybrid: ManagementHybrid;
    snapshot: DensitySimulationSnapshot;
    /** The habitual density: the wizard's input, or the saved value as text. */
    habitual: React.ReactNode;
    /** The % either way of the estimated environment; the thumb's value while dragging. */
    variability: { better: number; worse: number };
    slider?: React.ReactNode;
    headerAction?: React.ReactNode;
    selector?: React.ReactNode;
    /** Fixed chart width, for the PDF's server-side render, which has no layout to measure. */
    chartWidth?: number;
    /** Fewer density ticks, for a phone. */
    compact?: boolean;
}) {
    const units = useUnitProvider();
    const { rows, habitualDensity } = snapshot;

    const densityRange = useMemo(() => {
        const densities = hybrid.points.map((point) => point.density);
        return densities.length ? { min: Math.min(...densities), max: Math.max(...densities) } : undefined;
    }, [hybrid]);

    const formatDensity = (value?: number) =>
        value !== undefined ? units.toCornDensity(value, undefined).toString() : NOT_AVAILABLE;
    const formatYield = (value?: number) =>
        value !== undefined ? units.toCornYield(value, undefined).toString() : NOT_AVAILABLE;

    const estimatedRow = rows.find((row) => row.key === 'estimated');

    // A legacy record can hold different shifts either way; the new flow writes one to both.
    const symmetric = variability.better === variability.worse;
    const variabilityPrefix = symmetric ? '+/-' : null;
    const variabilityValue = symmetric ? `${variability.better}%` : `+${variability.better}% / -${variability.worse}%`;

    const axes = useMemo(() => {
        if (!densityRange) return {};
        const yields = [
            ...hybrid.points.map((point) => point.yield),
            ...rows.flatMap((row) => [row.recommendedYield, row.habitualYield]),
        ].filter((value): value is number => value !== undefined);
        try {
            return {
                density: planTicks({
                    min: densityRange.min,
                    max: densityRange.max,
                    toDisplay: (value) => units.toCornDensity(value, 6).value as number,
                    fromDisplay: (value) => units.fromCornDensity(value),
                    targetCount: compact ? 4 : 7,
                }),
                yield: planTicks({
                    min: Math.min(...yields),
                    max: Math.max(...yields),
                    toDisplay: (value) => units.toCornYield(value, 6).value as number,
                    fromDisplay: (value) => units.fromYield(value, 'corn'),
                    targetCount: 4,
                    extendDomain: true,
                }),
            };
        } catch {
            return {};
        }
    }, [hybrid, densityRange, rows, units, compact]);

    const recommendedCell = { backgroundColor: (theme: Theme) => theme.palette.primary.infoBackground };
    const habitualCell = { backgroundColor: HABITUAL_BACKGROUND };

    const chartRef = useRef<HTMLDivElement>(null);
    const [hovered, setHovered] = useState<HoveredDot>();
    // The tooltip sits beside its point, on whichever side has room.
    const tooltipOnLeft = !!hovered && hovered.cx > (chartRef.current?.offsetWidth ?? 0) / 2;

    const dot = (row: SimulationRow, density: number, yieldValue: number, fill: string) => (
        <ReferenceDot
            key={`${fill}-${row.key}`}
            x={density}
            y={yieldValue}
            ifOverflow='hidden'
            shape={(props: { cx?: number; cy?: number }) => (
                <MarkedDot
                    cx={props.cx}
                    cy={props.cy}
                    fill={fill}
                    onEnter={(position) => setHovered({ ...position, row, density, yieldValue })}
                    onLeave={() => setHovered(undefined)}
                />
            )}
        />
    );

    return (
        <Card elevation={0} variant='outlined' sx={{ borderRadius: '8px', p: 2 }}>
            <Stack spacing={3}>
                <Stack spacing={0.5}>
                    <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={2}>
                        <Typography variant='h6' color='primary'>
                            {_('Simulación de densidad y ambiente')}
                        </Typography>
                        {headerAction}
                    </Stack>
                    <Typography variant='body2' color='primary'>
                        {_(
                            'Simulá cómo cambia el rendimiento si el ambiente del lote viene mejor o peor de lo estimado'
                        )}
                    </Typography>
                </Stack>

                {selector}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        sx={{
                            flex: { md: '0 0 526px' },
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: (theme) => theme.palette.divider,
                        }}
                    >
                        <Stack flex={1} spacing={1} p={2} pr={3} sx={recommendedCell} justifyContent='space-between'>
                            <SectionLabel>{_('Densidad recomendada')}</SectionLabel>
                            <Typography variant='h5' color='primary' sx={{ minHeight: 40, alignContent: 'center' }}>
                                {formatDensity(hybrid.harvestDensity)}
                            </Typography>
                            <Figure label={_('Rendimiento')} value={formatYield(hybrid.estimatedYield)} />
                        </Stack>
                        <Stack flex={1} spacing={1} p={2} pl={3} sx={habitualCell} justifyContent='space-between'>
                            <Typography variant='overline' textTransform='uppercase' sx={{ color: HABITUAL_ACCENT }}>
                                {_('Densidad habitual')}
                            </Typography>
                            {habitual}
                            <Figure label={_('Rendimiento')} value={formatYield(estimatedRow?.habitualYield)} />
                        </Stack>
                    </Stack>

                    <Stack
                        p={2}
                        sx={{
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: (theme) => theme.palette.divider,
                        }}
                        direction='column'
                        justifyContent='space-between'
                    >
                        <Stack flex={1} spacing={2}>
                            <SectionLabel>{_('Variabilidad del ambiente en el lote')}</SectionLabel>
                            <Stack direction='row' spacing={3} alignItems='center'>
                                <Stack
                                    direction='row'
                                    spacing={0.5}
                                    alignItems='baseline'
                                    sx={{ width: slider ? 100 : 'auto', flexShrink: 0 }}
                                >
                                    <Typography variant='subtitle1' color='primary'>
                                        {variabilityPrefix}
                                    </Typography>
                                    <Typography variant='h5' color='primary'>
                                        {variabilityValue}
                                    </Typography>
                                </Stack>
                                {slider}
                            </Stack>
                        </Stack>
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_(
                                'Cuánto puede variar el ambiente del lote respecto del estimado, hacia arriba o hacia abajo.'
                            )}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack spacing={2}>
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('Curva comparativa')}
                    </Typography>
                    <Stack spacing={1}>
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_('Rendimiento estimado')} ({units.getUnit('yield', 'corn')})
                        </Typography>
                        <Box ref={chartRef} sx={{ position: 'relative' }}>
                            <ChartFrame width={chartWidth} height={CHART_HEIGHT}>
                                <LineChart
                                    data={hybrid.points}
                                    width={chartWidth}
                                    height={CHART_HEIGHT}
                                    margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                                >
                                    <CartesianGrid stroke={AXIS_COLOR} vertical={false} />
                                    <XAxis
                                        dataKey='density'
                                        type='number'
                                        domain={axes.density?.domain || ['dataMin', 'dataMax']}
                                        ticks={axes.density?.ticks}
                                        tickFormatter={(value: number) =>
                                            units.toCornDensity(value, undefined).localeValue
                                        }
                                        tickLine={false}
                                        axisLine={{ stroke: MARK_COLOR }}
                                        height={24}
                                    />
                                    <YAxis
                                        type='number'
                                        domain={axes.yield?.domain || ['dataMin', 'auto']}
                                        ticks={axes.yield?.ticks}
                                        tickFormatter={(value: number) =>
                                            units.toCornYield(value, undefined).localeValue
                                        }
                                        tickLine={false}
                                        axisLine={{ stroke: MARK_COLOR }}
                                        width={56}
                                    />
                                    <ReferenceLine x={hybrid.harvestDensity} stroke={MARK_COLOR} ifOverflow='hidden' />
                                    {habitualDensity !== undefined && (
                                        <ReferenceLine
                                            x={habitualDensity}
                                            stroke={MARK_COLOR}
                                            strokeDasharray='3 3'
                                            ifOverflow='hidden'
                                        />
                                    )}
                                    <Line
                                        type='monotone'
                                        dataKey='yield'
                                        stroke={CURVE_COLOR}
                                        strokeWidth={2}
                                        dot={false}
                                        activeDot={false}
                                        isAnimationActive={false}
                                    />
                                    {/* After the line, so the dots are on top and take the hover. */}
                                    {rows.map((row) =>
                                        row.recommendedYield !== undefined && hybrid.harvestDensity
                                            ? dot(
                                                  row,
                                                  hybrid.harvestDensity,
                                                  row.recommendedYield,
                                                  RECOMMENDED_DOT_COLOR
                                              )
                                            : null
                                    )}
                                    {rows.map((row) =>
                                        habitualDensity !== undefined && row.habitualYield !== undefined
                                            ? dot(row, habitualDensity, row.habitualYield, HABITUAL_DOT_COLOR)
                                            : null
                                    )}
                                </LineChart>
                            </ChartFrame>
                            {hovered && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: hovered.cy,
                                        left: hovered.cx,
                                        transform: tooltipOnLeft
                                            ? 'translate(calc(-100% - 12px), -50%)'
                                            : 'translate(12px, -50%)',
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <SimulationTooltip
                                        hybridName={hybrid.catalogName}
                                        environment={ENVIRONMENT_LABELS[hovered.row.key](
                                            formatYield(hovered.row.environment)
                                        )}
                                        density={formatDensity(hovered.density)}
                                        yieldValue={formatYield(hovered.yieldValue)}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Stack direction='row' justifyContent='space-between' flexWrap='wrap' gap={1}>
                            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                                <Typography variant='caption' color='primary'>
                                    {_('Referencias')}
                                </Typography>
                                <Stack direction='row' spacing={0.5} alignItems='center'>
                                    <LegendLine />
                                    <Typography variant='caption' color='primary'>
                                        {_('Densidad recomendada')}
                                    </Typography>
                                </Stack>
                                <Stack direction='row' spacing={0.5} alignItems='center'>
                                    <LegendLine dashed />
                                    <Typography variant='caption' color='primary'>
                                        {_('Densidad habitual')}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Typography variant='caption' color='primary.mui.inactive'>
                                {_('Densidad')} ({units.getUnit('density', 'corn')})
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 650 }}>
                        <DensitySimulationTable {...snapshot} />
                    </Box>
                </Box>
            </Stack>
        </Card>
    );
}

/** The habitual density as saved, laid out like the recommended one beside it. */
export function HabitualDensityText({ value }: { value?: number | null }) {
    const units = useUnitProvider();
    return (
        <Typography variant='h5' color='primary' sx={{ minHeight: 40, alignContent: 'center' }}>
            {value ? units.toCornDensity(value, undefined).toString() : NOT_AVAILABLE}
        </Typography>
    );
}

/**
 * "Simulación de densidad y ambiente": the recommended density against the grower's habitual
 * one, under the estimated environment and that environment shifted up and down.
 *
 * The habitual density and the variability are the crop config's `userDensity` and
 * `userBetterEnvironment`/`userWorseEnvironment`, so they are saved with the recommendation and
 * the result page and PDF can show the same table. The selected hybrid is only a view choice.
 */
function DensitySimulation({
    hybrids,
    environment,
    userDensity,
    variability: savedVariability,
    worseVariability = savedVariability,
    onChange,
    onCopy,
    copying,
    readOnly = false,
}: {
    hybrids: ManagementHybrid[];
    environment?: number;
    /** Plants/ha, as stored; null or 0 when the user hasn't given one. */
    userDensity?: number | null;
    /** The stored variability, in % either way of the estimated environment. */
    variability: number;
    /** The worse-environment shift, when a legacy record holds a different one; else the same. */
    worseVariability?: number;
    /**
     * Writes the inputs back to the crop config. `userDensity` is null when the field is emptied
     * or holds a density off the curve, so an unusable value is never saved.
     */
    onChange?: (values: { userDensity?: number | null; variability?: number }) => void;
    /**
     * Copies the yields table as an image, through the same block-image endpoint as the step's
     * other copy buttons. Gets the table's snapshot because the render can't recompute it — see
     * `DensitySimulationSnapshot`. Omitted until the draft recommendation exists; no button then.
     */
    onCopy?: (copy: DensitySimulationCopy) => void;
    copying?: boolean;
    /** The result page: the saved values as text, with nothing to edit or copy. */
    readOnly?: boolean;
}) {
    const units = useUnitProvider();
    const isMobile = useIsMobile();

    // Only hybrids with a curve can be simulated; table-backed products have nothing to read.
    const candidates = useMemo(
        () => hybrids.filter((hybrid) => hybrid.points.length > 0 && hybrid.harvestDensity),
        [hybrids]
    );
    const [selectedId, setSelectedId] = useState<number>();
    // Falls back to the first hybrid when the selected one is removed from the recommendation.
    const hybrid = candidates.find(({ id }) => id === selectedId) ?? candidates[0];

    // Seeded once from the stored value; after that the field owns its own text, so typing
    // "7," isn't reformatted under the cursor by the round trip through the crop config.
    const [habitualInput, setHabitualInput] = useState(() =>
        userDensity ? String(units.toCornDensity(userDensity, undefined).value) : ''
    );
    // The thumb moves with `variability`; requests and the saved value follow on release.
    const [variability, setVariability] = useState(savedVariability);
    useEffect(() => setVariability(savedVariability), [savedVariability]);

    const densityRange = useMemo(() => {
        const densities = hybrid?.points.map((point) => point.density) ?? [];
        return densities.length ? { min: Math.min(...densities), max: Math.max(...densities) } : undefined;
    }, [hybrid]);

    /** The input in plants/ha, and whether it falls on the curve the table reads it off. */
    const readHabitual = (input: string) => {
        const display = parseDensityInput(input);
        const raw = display !== undefined ? units.fromCornDensity(display) : undefined;
        const outOfRange = raw !== undefined && !!densityRange && (raw < densityRange.min || raw > densityRange.max);
        return { raw, outOfRange, usable: outOfRange ? undefined : raw };
    };
    const typed = readHabitual(habitualInput);
    const habitualOutOfRange = !readOnly && typed.outOfRange;
    const habitualDensity = readOnly ? userDensity || undefined : typed.usable;

    const handleHabitualChange = (value: string) => {
        const cleaned = value.replace(/[^0-9.,]/g, '');
        setHabitualInput(cleaned);
        onChange?.({ userDensity: readHabitual(cleaned).usable ?? null });
    };

    const { snapshot } = useDensitySimulation({
        hybrid,
        environment,
        betterPercent: savedVariability,
        worsePercent: worseVariability,
        habitualDensity,
    });
    const formatDensity = (value?: number) =>
        value !== undefined ? units.toCornDensity(value, undefined).toString() : NOT_AVAILABLE;

    if (!hybrid || !environment || !snapshot) return null;

    return (
        <DensitySimulationView
            hybrid={hybrid}
            snapshot={snapshot}
            compact={isMobile}
            variability={{
                better: readOnly ? savedVariability : variability,
                worse: readOnly ? worseVariability : variability,
            }}
            selector={<HybridRadioGroup hybrids={candidates} selectedId={hybrid.id} onSelect={setSelectedId} />}
            habitual={
                readOnly ? (
                    <HabitualDensityText value={userDensity} />
                ) : (
                    <TextField
                        value={habitualInput}
                        onChange={(event) => handleHabitualChange(event.target.value)}
                        error={habitualOutOfRange}
                        helperText={
                            habitualOutOfRange && densityRange
                                ? _('Ingresá una densidad entre {{min}} y {{max}}.', {
                                      min: formatDensity(densityRange.min),
                                      max: formatDensity(densityRange.max),
                                  })
                                : undefined
                        }
                        InputProps={{ endAdornment: units.getUnit('density', 'corn') }}
                        inputProps={{ inputMode: 'decimal', 'data-testid': 'habitual-density-input' }}
                        size='small'
                        fullWidth
                        sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                                top: '-10px',
                            },
                        }}
                    />
                )
            }
            slider={
                readOnly ? undefined : (
                    <Slider
                        value={variability}
                        min={0}
                        max={MAX_VARIABILITY}
                        step={1}
                        marks={VARIABILITY_MARKS}
                        size='small'
                        onChange={(_event, value) => setVariability(value as number)}
                        onChangeCommitted={(_event, value) => onChange?.({ variability: value as number })}
                        aria-label={_('Variabilidad del ambiente en el lote')}
                        sx={{
                            color: MARK_COLOR,
                            '& .MuiSlider-markLabel': { fontSize: 12, color: 'primary.main' },
                        }}
                    />
                )
            }
            headerAction={
                readOnly || !onCopy ? undefined : (
                    <Button
                        startIcon={
                            copying ? (
                                <CircularProgress size={18} />
                            ) : (
                                <ContentCopyIcon fontSize='small' sx={{ color: 'primary.onSurface' }} />
                            )
                        }
                        onClick={() =>
                            onCopy({
                                hybrid,
                                snapshot,
                                hybrids: candidates.map(({ id, catalogName }) => ({ id, catalogName })),
                                selectedId: hybrid.id,
                            })
                        }
                        disabled={copying}
                        size='small'
                        sx={{ flexShrink: 0 }}
                        data-testid='copy-block-image-density_simulation'
                    >
                        {isMobile ? null : _('Copiar gráfico')}
                    </Button>
                )
            }
        />
    );
}

export default DensitySimulation;
